import React, { useEffect, useMemo } from 'react';
import { bool, number, string, object } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import qs from 'query-string';
import history from 'history.js';

import useStyles from 'hooks/useStyles';
import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';

import SnapaskHeader from 'components/SnapaskHeader';
import CountdownDate from 'components/CountdownDate';
import ContentItem from 'components/ContentItem';
import { BackIcon } from 'components/Icon';
import Button from 'components/Button';
import Link from 'components/Link';

import { COURSE, REGULAR_CLASS, GROUP_PASS } from 'constants/contentTypes';
import {
  PREPACK_PURCHASE_TEMPLATE,
  COURSE_INFORMATION_TEMPLATE,
  LIVE_TOPIC,
} from 'constants/routingPath';
import { PURCHASE_PAYMENT_OPTION_PREPACK_PAGE_ENTER } from 'constants/gtmEvent';
import { PREPACK } from 'constants/checkoutCollectionTypes';
import { SUBSCRIPTION } from 'constants/checkoutCollectionGroupTypes';

import { getCheckoutCollectionContentListById } from 'reduxModules/checkoutCollection/selector';
import { push, goBack } from 'reduxModules/navigation/action';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';

// eslint-disable-next-line css-modules/no-unused-class
import checkoutCollectionStyles from 'styles/checkoutCollection.scss';
import styles from './Prepack.scss';

const renderPrice = ({
  t,
  price,
  hasUnit,
  className,
  TagName,
  billingCyclePerMonths,
}) => {
  return hasUnit ? (
    <TagName className={className}>
      {t('purchase.pricingDisplay.priceWithMonth', {
        price,
        perMonth: billingCyclePerMonths > 1 ? billingCyclePerMonths : '',
      })}
    </TagName>
  ) : (
    <TagName>
      {t('purchase.pricingDisplay.priceNoMonth', {
        price,
      })}
    </TagName>
  );
};

export default function Prepack({
  bannerImageUrl,
  description,
  displayPrice,
  hasPresale,
  id,
  plan,
  presaleExpiredAt,
  title,
}) {
  useStyles(styles, checkoutCollectionStyles);

  const { t } = useTranslation(['purchase']);

  const dispatch = useDispatch();
  const { location } = history;

  const getOfficialSiteURL = useGetOfficialSiteURL();

  const contentList = useSelector(getCheckoutCollectionContentListById(id));

  const { liveTopics, courses, groupPasses } = contentList;
  const { highlight, billingCyclePerMonths, planType } = plan;
  const { originalPrice, sellingPrice, discount } = displayPrice;
  /**
   * course=1 -> { course: 1 }
   * course=1,2&live_topic=5 -> { course: [1,2], live_topic:5 }
   */
  const hashObject = useMemo(
    () =>
      qs.parse(location?.hash, {
        arrayFormat: 'comma',
        parseNumbers: true,
      }),
    [location?.hash],
  );

  const [contentType] = Object.entries(hashObject)
    .filter(([key]) => key === COURSE || key === REGULAR_CLASS)
    .flat();

  const showPromotionSection = highlight || hasPresale;
  // TODO: i18n
  const hasUnit = planType === SUBSCRIPTION;

  useEffect(() => {
    /**
     * course=1 -> { course: 1 }
     */
    const contentTypeWithId = qs.parse(location?.hash, {
      arrayFormat: 'comma',
      parseNumbers: true,
    });
    const [contentType, contentId] = Object.entries(contentTypeWithId).flat();

    dispatch(
      sendGTMEvent({
        eventName: PURCHASE_PAYMENT_OPTION_PREPACK_PAGE_ENTER,
        data: {
          type: contentType,
          contentId,
          bundleType: PREPACK,
          checkoutCollectionId: id,
          section: location?.state?.from ? 'normal' : 'deeplink',
        },
      }),
    );
  }, [dispatch, id, location?.hash, location?.state?.from]);

  const renderPromotionLabel = () => {
    if (highlight)
      return (
        <div className={checkoutCollectionStyles.labelAccent}>{highlight}</div>
      );
    if (hasPresale)
      return (
        <div className={checkoutCollectionStyles.label}>
          {t('purchase.promoOfferTag.limited')}
        </div>
      );

    return null;
  };

  const handleGoPrepackPage = () =>
    dispatch(
      push({
        pathname: getOfficialSiteURL(PREPACK_PURCHASE_TEMPLATE, {
          id,
        }),
      }),
    );

  return (
    <>
      <SnapaskHeader />
      <main className={styles.container}>
        {history.length > 1 && (
          <BackIcon
            width={24}
            height={24}
            className={styles.backIcon}
            onClick={() => dispatch(goBack())}
          />
        )}
        <div className={styles.details}>
          <div className={styles.information}>
            <div>
              {showPromotionSection && (
                <div className={checkoutCollectionStyles.promotion}>
                  {renderPromotionLabel()}
                  {hasPresale && <CountdownDate dateTo={presaleExpiredAt} />}
                </div>
              )}
              <h4 className={styles.title}>{title}</h4>
              <p className={styles.description}>{description}</p>
            </div>
            <div className={styles.planContent}>
              <div className={styles.price}>
                <div>
                  {renderPrice({
                    price: sellingPrice,
                    TagName: 'div',
                    t,
                    hasUnit,
                    originalPrice,
                    billingCyclePerMonths,
                  })}
                </div>
                {discount &&
                  renderPrice({
                    price: originalPrice,
                    TagName: 'del',
                    className: styles.originalPrice,
                    t,
                    hasUnit,
                    originalPrice,
                    billingCyclePerMonths,
                  })}
              </div>
              <Button
                text={t('purchase.content.cta.getItNow')}
                block
                onClick={handleGoPrepackPage}
              />
            </div>
          </div>
          <div className={styles.banner}>
            <img src={bannerImageUrl} alt={title} />
          </div>
        </div>

        <h4 className={styles.contentListTitle}>
          {t('purchase.content.prePackedPlanDetailTitle')}
        </h4>
        <ul className={styles.contentList}>
          {liveTopics.map(liveTopic => (
            <Link
              to={getOfficialSiteURL(LIVE_TOPIC, {
                id: liveTopic.id,
              })}
              key={`${REGULAR_CLASS}-${liveTopic.id}`}
              newTab
              className={styles.contentLink}
            >
              <li>
                <ContentItem
                  showButton={false}
                  imageUrl={liveTopic.thumbnailUrl}
                  name={liveTopic.name}
                  contentType={contentType}
                  frequency={liveTopic.frequency}
                />
              </li>
            </Link>
          ))}

          {courses.map(course => (
            <Link
              to={getOfficialSiteURL(COURSE_INFORMATION_TEMPLATE, {
                id: course.id,
              })}
              key={`${COURSE}-${course.id}`}
              newTab
              className={styles.contentLink}
            >
              <li>
                <ContentItem
                  showButton={false}
                  imageUrl={course.thumbnailUrl}
                  name={course.name}
                  contentType={contentType}
                  totalLessonsDurationTime={course.totalLessonsDurationTime}
                  lessonCount={course.lessonCount}
                />
              </li>
            </Link>
          ))}
          {groupPasses.map(groupPass => (
            <li key={`${GROUP_PASS}-${groupPass.id}`}>
              <ContentItem
                showButton={false}
                imageUrl={groupPass.thumbnailUrl}
                name={groupPass.name}
                contentType={contentType}
                description={t('purchase.content.cta.bundleTotal', {
                  course: groupPass.courseAmount,
                })}
              />
            </li>
          ))}
        </ul>
      </main>
    </>
  );
}

Prepack.propTypes = {
  id: number.isRequired,
  title: string.isRequired,
  hasPresale: bool.isRequired,
  plan: object.isRequired,
  bannerImageUrl: string,
  description: string,
  displayPrice: object,
  presaleExpiredAt: string,
};

Prepack.defaultProps = {
  bannerImageUrl: undefined,
  description: undefined,
  displayPrice: undefined,
  presaleExpiredAt: undefined,
};
